import React, { FC, MouseEventHandler, useCallback, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'next-i18next';
import { CPagination, CTabs } from '@web-kc/shared/ui';
import { ChevronDown as ArrowDownIcon } from '@web-shared/core/icons';
import { Layout, LotCard, TenderCard } from '@web-kc/shared/components';
import { useLots, useTenders } from '@web-kc/core/api';
import { useApp } from '@web-kc/core/providers';
import LotSkeleton from './LotSkeleton';
import Search from '@mui/icons-material/Search';
import { useRouter } from 'next/router';
import LotAndTenderSearchFiltersRefactor from '../components/LotAndTenderSearchFiltersRefactor';
import { useFiltersQuery } from './parser';

interface Props {
  type: 'lots' | 'tenders';
}

export const PublicTenders: FC<Props> = ({ type }) => {
  const isMp = useApp() === 'rti';
  const theme = useTheme();
  const { t } = useTranslation(['tenders', 'common']);
  const { push, query } = useRouter();
  const [searchFilters, setSearchFilters] = useFiltersQuery({
    page: 0,
    size: 10,
    sort: 'CREATED_DESC',
  });

  const { data: lotsData, isLoading: loadingLots } = useLots(
    searchFilters as any,
    type === 'lots',
  );

  const { data: tendersData, isLoading: loadingTenders } = useTenders(
    searchFilters as any,
    type === 'tenders',
  );

  const isLoading = loadingLots || loadingTenders;
  const pages = (type === 'lots' ? lotsData : tendersData)?.pages ?? 0;

  const [isSortingIconUp, setIsSortingIconUp] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const sortingNaming = [
    {
      id: 'CREATED_DESC',
      name: t('tenders.sortNamings.createdDesc'),
    },
    {
      id: 'EXPIRED_ASC',
      name: t('tenders.sortNamings.expiredAsc'),
    },
    {
      id: 'PRICE_ASC',
      name: t('tenders.sortNamings.priceAsc'),
    },
    {
      id: 'PRICE_DESC',
      name: t('tenders.sortNamings.priceDesc'),
    },
  ];

  const handeChangePageNumber = (page: number) => {
    setSearchFilters({ ...searchFilters, page: page - 1 });
  };

  const handleSortingClick = useCallback<MouseEventHandler<HTMLDivElement>>(
    (event) => {
      setAnchorEl(event.currentTarget);
      setIsSortingIconUp((prev) => !prev);
    },
    [],
  );

  const handleSortingClose = useCallback(() => {
    setAnchorEl(null);
    setIsSortingIconUp(true);
  }, []);

  const tabs = [
    {
      title: t('tenders.tabs.lots', {
        count: (type === 'lots' ? lotsData : tendersData)?.lots ?? 0,
      }),
      key: 'lots',
    },
    {
      title: t('tenders.tabs.tenders', {
        count: (type === 'lots' ? lotsData : tendersData)?.tenders ?? 0,
      }),
      key: 'tenders',
    },
  ];

  return (
    <Layout
      paths={['/', isMp ? '/lots' : '']}
      labels={[t('breadcrumbs.main'), isMp ? t('breadcrumbs.lots') : '']}
      pageTitle={t('pageTitle.lots', { ns: 'common' })}
    >
      <Typography variant="h1">
        {t(`tenders.title_${isMp ? 'rti' : 'kc'}`)}
      </Typography>

      <LotAndTenderSearchFiltersRefactor
        initialFilters={searchFilters}
        onApply={(newFilters) => {
          setSearchFilters(newFilters, { replace: true });
        }}
      />

      {isLoading && <LotSkeleton />}

      {!isLoading && (
        <Box>
          <Stack direction="row" justifyContent="space-between" mt="50px">
            <Stack
              direction={{ xs: 'column', md: 'row', lg: 'row' }}
              justifyContent="space-between"
              width={'100%'}
            >
              <Stack
                direction="row"
                gap="20px"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Typography variant="h3" fontSize="20px !important">
                  {t('tenders.found')}
                </Typography>
                <Box
                  sx={{
                    width: 'fit-content',
                    '& .MuiTabs-root': {
                      minHeight: '38px',
                      height: '38px',
                      '& .MuiButtonBase-root': {
                        paddingTop: 0,
                        paddingBottom: '9px',
                      },
                    },
                  }}
                >
                  <CTabs
                    tabs={tabs}
                    tabLabelKey="title"
                    tabValueKey="key"
                    size={isMp ? 'medium' : 'small'}
                    sx={{
                      '& button': {
                        fontWeight: 'bold',
                        fontSize: '18px',
                        textTransform: 'lowercase',
                        fontFamily: theme.typography.h4.fontFamily,
                      },
                    }}
                    value={type}
                    onChange={(type) => push({ pathname: `/${type}`, query })}
                  />
                </Box>
              </Stack>
              <Stack
                direction="row"
                gap="30px"
                alignItems="center"
                justifyContent="flex-end"
                mt={{ xs: '30px', md: 0, lg: 0 }}
              >
                <Stack
                  direction="row"
                  sx={{
                    color: isMp
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    cursor: 'pointer',
                  }}
                  alignItems="center"
                  gap="10px"
                  onClick={handleSortingClick}
                >
                  <Typography>
                    {searchFilters.sort
                      ? sortingNaming.filter(
                          (option) => option.id === searchFilters.sort,
                        )[0]?.name
                      : sortingNaming.filter(
                          (option) => option.id === 'CREATED_DESC',
                        )[0]?.name}
                  </Typography>
                  {isSortingIconUp ? (
                    <ArrowDownIcon />
                  ) : (
                    <ArrowDownIcon style={{ transform: 'rotate(180deg)' }} />
                  )}
                </Stack>
                <Box
                  sx={{
                    color: '#999999',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {t('tenders.volume')}
                </Box>
              </Stack>
            </Stack>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleSortingClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              sx={{ cursor: 'pointer' }}
            >
              <List sx={{ padding: 0 }}>
                {sortingNaming.map((option, idx) => (
                  <ListItem
                    disablePadding
                    key={option.id ?? idx}
                    onClick={() => {
                      setSearchFilters({
                        ...searchFilters,
                        page: 0,
                        sort: option?.id as 'CREATED_DESC',
                      });
                      handleSortingClose();
                    }}
                  >
                    <ListItemButton>
                      <ListItemText primary={option.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Popover>
          </Stack>
          {type === 'lots' && lotsData?.total === 0 ? (
            <Stack direction="row" gap={2} alignItems="center" mt={5}>
              <Search color="info" sx={{ width: '32px', height: '32px' }} />
              <Typography color="info.main" fontSize={22} fontWeight={500}>
                {t('tenders:tenders.notFound.lots')}
              </Typography>
            </Stack>
          ) : (
            type === 'tenders' &&
            tendersData?.total === 0 && (
              <Stack direction="row" gap={2} alignItems="center" mt={5}>
                <Search color="info" sx={{ width: '32px', height: '32px' }} />
                <Typography color="info.main" fontSize={22} fontWeight={500}>
                  {t('tenders:tenders.notFound.tenders')}
                </Typography>
              </Stack>
            )
          )}
          <Box mt="20px" key={type}>
            {type === 'lots'
              ? lotsData?.content?.map((row) => (
                  <LotCard key={row.id} lotInfo={row} />
                ))
              : tendersData?.content?.map((tender) => (
                  <TenderCard key={tender.id} group={tender} />
                ))}
          </Box>
          <Box mt="30px">
            {pages > 1 && (
              <Stack direction="row" alignItems="center" gap="20px">
                <Box mb={4}>
                  <CPagination
                    count={pages}
                    isTextShown="desktop-only"
                    currentPage={
                      searchFilters.page ? Number(searchFilters.page) + 1 : 1
                    }
                    handlePageChange={handeChangePageNumber}
                  />
                </Box>
              </Stack>
            )}
          </Box>
        </Box>
      )}
    </Layout>
  );
};
